import React, { useState, useCallback, useEffect, useRef } from 'react';
import SalesActionStat from '../../components/salesActionStat/SalesActionStat';
import { useSnackbar } from 'notistack'; 
import { getActionStat, getDepartmentAndUserList, getActionStatList } from '../../lib/api/salesAction';
import { getHolidayList } from '../../lib/api/holidayAction';

const SalesActionStatContainer = ({ toggleCalFull, isCalFull }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [statEvents, setStatEvents] = useState([]);
    const [actionEvents, setActionEvents] = useState([]);
    const [holidayList, setHolidayList] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());

    const [dptUuid, setDptUuid] = useState('');
    const [usrUuid, setUsrUuid] = useState('');
    
    const [dptList, setDptList] = useState([]);
    const [usrList, setUsrList] = useState([]);

    // 💡 여러 연도 범위에 대한 중복 API 호출을 효율적으로 차단하기 위한 캐싱용 Ref 변수
    const fetchedYearsRef = useRef(new Set());

    // 🔍 1. 시작 연도와 종료 연도를 전달받아 필요한 구간의 휴일을 적재하는 내부 함수
    const fetchHolidaysInRange = useCallback(async (startYearStr, endYearStr) => {
        if (!startYearStr || !endYearStr) return;

        try {
            // 조회 시작 연도부터 종료 연도까지 배열 생성 (예: ['2026', '2027'])
            const startYear = Number(startYearStr);
            const endYear = Number(endYearStr);
            const yearsToFetch = [];

            for (let y = startYear; y <= endYear; y++) {
                const yStr = String(y);
                // 이미 가지고 있는 연도 데이터가 아닐 때만 API 요청 목록에 추가
                if (!fetchedYearsRef.current.has(yStr)) {
                    yearsToFetch.push(yStr);
                }
            }

            // 추가로 요청해야 할 연도가 없다면 리턴
            if (yearsToFetch.length === 0) return;

            // 🚀 비동기 병렬 처리를 통해 필요한 모든 연도의 휴일을 병합 조회
            const responses = await Promise.all(
                yearsToFetch.map(year => getHolidayList(year))
            );

            // 가공하여 적재할 임시 공휴일 컨테이너 배열
            let newHolidays = [];

            responses.forEach((res) => {
                const list = res?.data?.list || res?.data?.data || [];
                newHolidays = [...newHolidays, ...list];
            });

            // 기존 휴일 리스트에 새 연도 데이터를 추가하여 상태 업데이트
            setHolidayList(prev => {
                const combined = [...prev, ...newHolidays];
                // 고유 식별코드나 날짜 조합을 기반으로 한 고유 중복 제거 처리
                const uniqueMap = new Map();
                combined.forEach(h => {
                    const uniqueKey = h.holidayDate || h.id || JSON.stringify(h);
                    uniqueMap.set(uniqueKey, h);
                });
                return Array.from(uniqueMap.values());
            });

            // 페칭이 완료된 연도들을 캐시 세트(Set)에 누적 기록
            yearsToFetch.forEach(year => fetchedYearsRef.current.add(year));

        } catch (err) {
            console.error('검색 구간 휴일 목록 로드 오류:', err);
        }
    }, []);

    // 🔍 2. 통계 및 목록 데이터 로드 함수 (내부에서 시작/종료 연도 계산)
    const handleFetchStat = useCallback(async (startDate, endDate, targetDptUuid, targetUsrUuid, targetLoginUuid) => {
        if (!startDate || !endDate) return;

        // 📅 [요구사항 반영]: startDate와 endDate에서 각각 앞 4자리 문자열(연도)을 추출하여 전달
        const startYearStr = String(startDate).substring(0, 4);
        const endYearStr = String(endDate).substring(0, 4);
        fetchHolidaysInRange(startYearStr, endYearStr);

        const currentDptUuid = targetDptUuid !== undefined ? targetDptUuid : dptUuid;
        const currentUsrUuid = targetUsrUuid !== undefined ? targetUsrUuid : usrUuid;
        
        let currentLoginUuid = targetLoginUuid;
        if (currentLoginUuid === undefined && !currentDptUuid && !currentUsrUuid) {
            const localUser = localStorage.getItem('user');
            if (localUser) {
                const parsedUser = JSON.parse(localUser);
                currentLoginUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
            }
        }
        
        try {
            const [statResponse, listResponse] = await Promise.all([
                getActionStat(currentDptUuid, currentUsrUuid, startDate, endDate, currentLoginUuid),
                getActionStatList(currentDptUuid, currentUsrUuid, startDate, endDate, currentLoginUuid)
            ]);

            const statData = statResponse?.data?.list || [];
            const listData = listResponse?.data?.list || [];

            setStatEvents(statData);
            setActionEvents(listData);
        } catch (err) {
            console.error('영업활동 통계 및 목록 로드 오류:', err);
            const message = err.response?.data?.message || err.message || '데이터를 불러오는 중 오류가 발생했습니다.';
            enqueueSnackbar(message, { variant: 'error' });
        }
    }, [dptUuid, usrUuid, enqueueSnackbar, fetchHolidaysInRange]);

    // 🔍 3. 최초 마스터 데이터 조회 함수 (부서 및 사원 정보 획득 전담)
    const fetchInitialMasterData = useCallback(async () => {
        try {
            if (typeof getDepartmentAndUserList !== 'function') return;

            const localUser = localStorage.getItem('user');
            let loginUsrUuid = '';
            if (localUser) {
                const parsedUser = JSON.parse(localUser);
                loginUsrUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
            }

            const response = await getDepartmentAndUserList(loginUsrUuid);
            
            const fetchedDptList = response?.data?.dptList || [];
            const fetchedUsrList = response?.data?.usrList || [];

            setDptList(fetchedDptList);
            setUsrList(fetchedUsrList);

        } catch (err) {
            console.error('초기 조직도 마스터 데이터 로드 오류:', err);
            enqueueSnackbar('기본 정보 데이터를 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        fetchInitialMasterData();
    }, [fetchInitialMasterData]);

    return (
        <SalesActionStat 
            statEvents={statEvents}
            actionEvents={actionEvents}
            holidayList={holidayList}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            dptUuid={dptUuid}
            setDptUuid={setDptUuid}
            usrUuid={usrUuid}
            setUsrUuid={setUsrUuid}
            dptList={dptList}
            usrList={usrList}
            onFetchStat={handleFetchStat} 
        />
    );
};

export default SalesActionStatContainer;
